import React from 'react';

import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { ISingleSmallCardResponse } from 'api/queries/getListFilteredCards.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { DisplayStyle } from 'components/common/ToggleView/ToggleView';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { useRowsBoardLogic } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.logic';
import { useRowsBoardProvider } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoard.provider';
import {
    columnsV8,
    initialState,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/RowsBoardColumns.utils';
import { XlsExportModal } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/RowsBoard/XlsExportModal/XlsExportModal';
import { IComponentProps, IRowsBoard } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Button } from 'components/DesignSystem/Button';

export const RowsBoard = ({
    projectId,
    contractId,
    latestVersionId,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    globalFilterData,
    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title: propTitle,
}: IComponentProps) => {
    const { componentKey, routeId: id, endpoints, name: configTitle } = useConfigurationStore().battlecardsRowsBoard;
    const titleToUse = propTitle || configTitle;

    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    // Provider
    const { ui, api } = useRowsBoardProvider({
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        localFilters,
        globalFilterData,
        table,
        endpoints,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: titleToUse,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.listCardsData.payload,
        widgetEndpoint: api.listCardsData.endpoint,
    });

    // Logic
    const { logic } = useRowsBoardLogic({ ui, api, title: titleToUse, subTitle, table, latestVersionId });

    if (ui.state.displayStyle !== DisplayStyle.ROWS && !ui.isInPinboard) return null;

    return (
        <WidgetWithTitle
            id={id}
            title={
                <WidgetTitle
                    title={editableTitle}
                    setTitle={setEditableTitle}
                    widgetViewMode={widgetViewMode}
                    showPostfixInEditMode={true}
                    postfix={`(${api.listCardsData.instance.data?.numOfAllCards ?? 0})`}
                />
            }
            titleComponents={[
                logic.isStatusChangedFilterSelected ? (
                    <Button variant={'primary'} onClick={logic.runSimulation}>
                        Run Simulation
                    </Button>
                ) : null,
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddToReport
                    key={'addWidgetToReport'}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    projectId={projectId}
                    contractId={contractId}
                    uiState={ui.state}
                    shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                    callBack={table.setWidgetColumns}
                    apiPayload={api.listCardsData.payload}
                    widgetEndpoint={api.listCardsData.endpoint}
                />,
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ExportDropdown
                        key={'ExportDropdown'}
                        title={titleToUse}
                        pptxData={logic.pptxData}
                        ref={logic.boundaryRef}
                        subTitle={subTitle}
                        handleAPICsvExport={logic.handleAPICsvExportPrivilege ? logic.handleAPICsvExport : undefined}
                        handleAPICsvExportCompact={() => {
                            logic.handleAPICsvExport({ isCompact: true });
                        }}
                        handleAPIXlsExport={() => logic.setIsXlsExportModal(true)}
                        handleExrExport={() => logic.handleXerOrMppExport({ exportType: 'XER' })}
                        handleMppExport={() => logic.handleXerOrMppExport({ exportType: 'MPP' })}
                    />
                ),
                !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                    <ColumnsManager
                        onOpenModal={() => table.setModalColumnsOpen(true)}
                        onHideModal={() => table.setModalColumnsOpen(false)}
                        showModal={table.modalColumnsOpen}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        tableInstance={table.tableInstance}
                        initialState={initialState}
                        columns={columnsV8}
                        onChangeOrder={table.setColumnOrder}
                        onChangeVisibility={table.setColumnVisibility}
                        widgetViewMode={widgetViewMode}
                    />
                ),
                !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                    <ThreeDotsMenu
                        widgetViewMode={widgetViewMode}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        shareTitle={titleToUse}
                        shareLink={route}
                    />
                ),
            ]}
            titleFilters={[
                <ReportingVersionAndPills
                    key={'ReportingVersionAndPills'}
                    selectedProjectAndContract={widgetSelectedProjectAndContract}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
                <Narrative key={'narrative'} editNarrative={editNarrative} />,
                <PinboardPills
                    key={'pinboardPills'}
                    isInPinboard={WidgetViewModeUtils.isPinboardView(widgetViewMode)}
                    pills={ui.pills}
                    widgetViewMode={widgetViewMode}
                />,
            ]}
        >
            <TableContainer>
                {table.updatedInitialState && (
                    <TableV8<ISingleSmallCardResponse>
                        data={api.listCardsData.instance.data?.cardList || []}
                        columns={columnsV8}
                        isLoading={api.listCardsData.instance.isFetching || logic.exportIsLoading}
                        noData={<NoData>No activities match the applied filter</NoData>}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                        displayPaginationBottomBar
                        totalPages={table.totalPages}
                        setPagination={table.setPagination}
                        pagination={table.pagination}
                        isPaginationDisabled={api.listCardsData.instance.isFetching}
                    />
                )}
            </TableContainer>

            <XlsExportModal
                isXlsExportModal={logic.isXlsExportModal}
                setIsXlsExportModal={logic.setIsXlsExportModal}
                handleAPIXlsExport={logic.handleAPIXlsExport}
            />
        </WidgetWithTitle>
    );
};
